import React, { useState, useEffect } from "react";
import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";
import { validateToken } from "../tokenValidation";
import { toast, ToastContainer } from "react-toastify";
import { FaClock, FaArrowLeft } from "react-icons/fa";
import secureStorage from "../utils/secureStorage";

const Appointment = () => {
  const [statusFilter, setStatusFilter] = useState("OPEN");
  const [pageNumber, setPageNumber] = useState(0);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedHipId, setSelectedHipId] = useState("");
  const [subcentres, setSubcentres] = useState([]);


  // Modal state
  const [registerModalVisible, setRegisterModalVisible] = useState(false);
  const [formData, setFormData] = useState({});

  const API_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  // Fetch appointments
  const fetchAppointments = async () => {
    setLoading(true);
    try {
      const token = secureStorage.get("authToken");
      if (!token) return navigate("/login");
      const res = await fetch(
        `${API_URL}hospital/appointments?pageNumber=${pageNumber}&filter=${statusFilter}&hipId=${selectedHipId}`,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", "X-HIP-ID": secureStorage.get("clinic") } }
      );
      if (res.ok) {
        const json = await res.json();
        setAppointments(json.data.content || []);
        setTotalPages(json.data.totalPages);
        setTotalElements(json.data.totalElements);
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to load appointments");
    } finally {
      setLoading(false);
    }
  };

const fetchSubcentres = async () => {
  try {
    const token = secureStorage.get("authToken");
    const clinicId = secureStorage.get("clinic");
    const orgId = secureStorage.get("orgId");

    const response = await fetch(`${API_URL}hospital/get-subcentres?orgId=${orgId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "X-HIP-ID": clinicId,
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) throw new Error("Failed to fetch subcentres");

    const json = await response.json();
    console.log("Subcentre response:", json); // for debug

    const facilityDetails = json.data?.facilityDetails || [];

    const centres = facilityDetails.map(fac => ({
      hipId: fac.hipId,
      name: fac.centerName || fac.nameAlias || "Unnamed Centre"
    }));

    setSubcentres(centres);
    if (centres.length > 0) {
      setSelectedHipId(centres[0].hipId); // Default selection
    }
  } catch (e) {
    console.error(e);
    toast.error("Unable to load subcentres.");
  }
};

  useEffect(() => {
    validateToken(navigate);
    fetchSubcentres();
  }, [navigate]);

  useEffect(() => {
    if (selectedHipId) fetchAppointments();
  }, [statusFilter, pageNumber, selectedHipId]);


  // Open modal with data
  const openRegisterModal = (appt) => {
    setFormData({
      name: appt.name,
      abhaAddress: appt.abhaAddress,
      abhaNumber: appt.abhaNumber,
      token: appt.tokenNumber,
      bookedDateTime: appt.bookedDateTime,
      mobileNumber: appt.phoneNumber,
      gender: appt.gender,
      addressLine: appt.addressLine,
      district: appt.district,
      state: appt.state,
      dayOfBirth: appt.dayOfBirth,
      monthOfBirth: appt.monthOfBirth,
      yearOfBirth: appt.yearOfBirth,
    });
    setRegisterModalVisible(true);
  };

  // inside your component, before return()
  const formattedDOB = () => {
    const { dayOfBirth, monthOfBirth, yearOfBirth } = formData;
    if (!dayOfBirth || !monthOfBirth || !yearOfBirth) return "";
    return DateTime.fromObject({
      day: Number(dayOfBirth),
      month: Number(monthOfBirth),
      year: Number(yearOfBirth)
    }).toFormat("d LLLL yyyy"); // → "25 June 2000"
  };

  const closeRegisterModal = () => setRegisterModalVisible(false);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((f) => ({ ...f, [name]: value }));
  };

  // Submit registration API
  const submitRegistration = async () => {
    setLoading(true);
    try {
      if (formData.mobileNumber) {
        formData.phoneNumber = formData.mobileNumber;
      }
      formData.hipId = selectedHipId;
      const token = secureStorage.get("authToken");
      if (!token) return navigate("/login");
      const res = await fetch(`${API_URL}hospital/register-user`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", "X-HIP-ID": secureStorage.get("clinic") },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        toast.success("Registration successful");
        fetchAppointments();
        closeRegisterModal();
      } else {
        const err = await res.json();
        toast.error(err.data || "Registration failed");
      }
    } catch (e) {
      console.error(e);
      toast.error("Unexpected error during registration");
    } finally {
      setLoading(false);
    }
  };

  const isWithinOneHour = (iso) => {
    const now = DateTime.now().setZone("Asia/Kolkata");
    const then = DateTime.fromISO(iso, { zone: "utc" }).setZone("Asia/Kolkata");
    const diff = now.diff(then, "minutes").minutes;
    return diff >= 0 && diff <= 60 ? Math.floor(60 - diff) : null;
  };

  const filtered = appointments.filter((a) =>
    a.tokenNumber.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="bg-[#F6F6F6] min-h-screen p-6">
      {/* Header */}
      <div className="flex items-center mb-4 relative">
        <button
          onClick={() => navigate(-1)}
          className="absolute left-0 flex items-center text-teal-700 hover:text-teal-900 font-medium"
        >
          <FaArrowLeft className="mr-2" /> Back
        </button>
        <h1 className="w-full text-center text-3xl font-bold">Patient Appointments</h1>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row md:justify-between mb-4 gap-4">
        <div className="flex gap-2 items-center">
          <label className="text-l font-semibold text-gray-700">Select Facility :</label>
          <select
            value={selectedHipId}
            onChange={(e) => setSelectedHipId(e.target.value)}
            className="p-2 border rounded"
          >
            {subcentres.map((c) => (
              <option key={c.hipId} value={c.hipId}>{c.name}</option>
            ))}
          </select>
        </div>

        <div className="flex gap-2 items-center">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="p-2 border rounded">
            <option value="OPEN">Open</option>
            <option value="CLOSED">Closed</option>
          </select>
          <input
            type="text"
            placeholder="Search by Token"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="p-2 border rounded-l"
          />
          <button onClick={fetchAppointments} className="px-4 bg-teal-500 text-white rounded-r">
            🔍
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="relative">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
            <div className="w-16 h-16 border-4 border-t-teal-600 rounded-full animate-spin" />
          </div>
        )}
        <table className="w-full bg-[#DEEBEC] rounded-lg overflow-hidden shadow">
          <thead className="border-b border-teal-700">
            <tr>
              {['Date', 'Token', 'Name', 'ABHA #', 'ABHA Address', 'Phone', 'Action'].map(h => (
                <th key={h} className="px-4 py-2 text-left font-semibold text-teal-700">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="p-4 text-center text-gray-500">No appointments found.</td></tr>
            )}
            {filtered.map(a => {
              const left = isWithinOneHour(a.bookedDateTime);
              const expired = left === null;
              return (
                <tr key={a.id} className="border-b">
                  <td className="px-4 py-2 text-gray-700 text-sm">
                    {DateTime.fromISO(a.bookedDateTime, { zone: 'utc' }).setZone('Asia/Kolkata').toLocaleString(DateTime.DATE_MED)}
                  </td>
                  <td className="px-4 py-2 text-gray-700 text-sm">{a.tokenNumber}</td>
                  <td className="px-4 py-2 text-gray-700 text-sm">{a.name}</td>
                  <td className="px-4 py-2 text-gray-700 text-sm">{a.abhaNumber}</td>
                  <td className="px-4 py-2 text-gray-700 text-sm">{a.abhaAddress}</td>
                  <td className="px-4 py-2 text-gray-700 text-sm">{a.phoneNumber}</td>
                  <td className="px-4 py-2">
                    {expired ? (
                      <span className="text-gray-400">Expired</span>
                    ) : a.userExist ? (
                      <span className="text-gray-500">Registered User</span>
                    ) : (
                      <button
                        onClick={() => openRegisterModal(a)}
                        className="text-teal-700"
                      >
                        Register
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Register Modal */}
      {registerModalVisible && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm z-50"
        >
          <div
            className="relative bg-white rounded-xl shadow-2xl ring-1 ring-black ring-opacity-5 max-w-2xl w-full mx-4 grid grid-cols-1 md:grid-cols-2 gap-6 p-6 overflow-hidden"
          >
            {/* Close button */}
            <button
              onClick={closeRegisterModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>

            {/* Left panel: read-only info */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-gray-800">Patient Info</h3>
              <ul className="space-y-5">
                {[
                  ["Name", formData.name],
                  ["Date of Birth", formattedDOB()],
                  ["ABHA Address", formData.abhaAddress],
                  ["ABHA Number", formData.abhaNumber],
                  ["Token #", formData.token],
                  [
                    "Booked",
                    DateTime.fromISO(formData.bookedDateTime, { zone: "utc" })
                      .setZone("Asia/Kolkata")
                      .toLocaleString(DateTime.DATETIME_MED),
                  ],

                ].map(([label, val]) => (
                  <li key={label}>
                    <p className="text-xs uppercase text-gray-500">{label}</p>
                    <p className="mt-1 text-sm font-medium text-gray-900">{val}</p>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right panel: editable form */}
            <div className="space-y-5 overflow-y-auto max-h-[70vh] pr-2">
              <h3 className="text-xl font-semibold text-gray-800">Complete Registration</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700">Mobile Number</label>
                <input
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleFormChange}
                  className="mt-1 w-full border rounded-lg p-2 focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Gender</label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleFormChange}
                  className="mt-1 w-full border rounded-lg p-2 focus:ring-2 focus:ring-teal-500"
                >
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                  <option value="NA">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Address</label>
                <textarea
                  name="addressLine"
                  value={formData.addressLine}
                  onChange={handleFormChange}
                  rows={2}
                  className="mt-1 w-full border rounded-lg p-2 focus:ring-2 focus:ring-teal-500 "
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">District</label>
                  <input
                    name="district"
                    value={formData.district}
                    onChange={handleFormChange}
                    className="mt-1 w-full border rounded-lg p-2 focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">State</label>
                  <input
                    name="state"
                    value={formData.state}
                    onChange={handleFormChange}
                    className="mt-1 w-full border rounded-lg p-2 focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>
            </div>

            {/* Actions bar spans both columns */}
            <div className="md:col-span-2 flex justify-end space-x-4 pt-4 border-t">
              <button
                onClick={closeRegisterModal}
                className="px-6 py-2 bg-gray-100 rounded-lg font-medium hover:bg-gray-200"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={submitRegistration}
                className="px-6 py-2 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700"
                disabled={loading}
              >
                Register
              </button>
            </div>

            {/* Loading overlay */}
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-60 rounded-xl">
                <div className="w-16 h-16 border-4 border-t-teal-600 rounded-full animate-spin" />
              </div>
            )}
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default Appointment;
